/**
 * Where a signed-in person belongs when the app cannot use the URL they asked
 * for (wrong portal, unknown route, missing scope).
 *
 * The public landing page is NOT an answer for someone who has just signed in
 * — that was the "login sends me back to the home page" bug. It is only used
 * for visitors with no session at all.
 *
 * iter 624 — and neither is a route that will bounce them straight back here.
 * Four of the seven entries in this map pointed at a route that refuses the
 * very user type being sent to it, so `homeFor` and `RequireAuth` redirected
 * each other forever:
 *
 *   admin            -> /admin             is `superOnly`; a Pastor, Treasurer,
 *                                          General Secretary or Registrar is
 *                                          not super and has no `tenant_id`,
 *                                          so /admin sent them to /admin.
 *   security_officer -> /security/gate      no such route exists at all; the
 *                                          catch-all sent them back here.
 *   dining_staff     -> /pos               `type="admin"`, and their token
 *   housing_staff    -> /housing            type is `dining_staff` /
 *   hospital_staff   -> /admin/hospitals     `housing_staff` / `hospital_staff`.
 *
 * So every destination below is a route that has been checked to accept the
 * user type that is sent to it. `/home` (AdaptiveHomeDashboard) is the honest
 * answer whenever a type has no portal of its own that will admit it: it is
 * `RequireAuth` with no `type`, so it accepts any signed-in person, and it is
 * built to show someone what they can actually reach.
 *
 * `backend/tests/test_iter624_login_landing_loops.py` parses this file and
 * `AppRoutes.jsx` and fails if any entry here points at a route that would
 * refuse it. Add a destination only if it passes that test.
 */

/** Routes verified to accept the user type keyed against them. */
const HOME_BY_TYPE = {
  // `/admin` is superOnly — see `homeFor` for the super-admin case.
  admin: '/home',
  member: '/member',
  delegate: '/delegate',
  // RequireSecurityAuth accepts `security_officer` and `admin` tokens.
  security_officer: '/security/dashboard',
  // RequireAuth with no `type`, so a housing_staff token is admitted.
  housing_staff: '/housing/staff',
  // No dining or POS route admits a `dining_staff` token — /pos, /dining and
  // /kitchen are all `type="admin"`. Until one does, the shared home is right.
  dining_staff: '/home',
  // /admin/hospitals is superOnly and /admin/hospital/:id needs an id we do
  // not have here.
  hospital_staff: '/home',
};

function isSuperAdmin(user) {
  if (!user) return false;
  return user.is_super_admin === true
    || user.role === 'super_admin'
    || user.role === 'platform_admin'
    || (Array.isArray(user.permissions)
        && (user.permissions.includes('*') || user.permissions.includes('super_admin')));
}

export function homeFor(userType, user) {
  const tid = user?.tenant_id;
  if (userType === 'teacher') return tid ? `/school/${tid}/teacher` : '/home';
  if (userType === 'student') return tid ? `/school/${tid}/student` : '/login';
  if (userType === 'parent' || userType === 'guardian') return tid ? `/school/${tid}/parent` : '/login';
  if (userType === 'applicant') return tid ? `/apply/${tid}` : '/login';

  // The whole admin console is the super admin's home. Everybody else holding
  // an admin session gets the shared dashboard, because /admin is superOnly
  // and would send them straight back here.
  if (userType === 'admin') {
    if (isSuperAdmin(user)) return '/admin';
    if (tid) return `/admin/schools/${tid}`;
    return '/home';
  }

  return HOME_BY_TYPE[userType] || '/home';
}

export default homeFor;
